/**
 * Bulk actions on All Clients / New Leads — the sticky bar shown while rows are
 * selected, and the confirm dialog every action goes through.
 *
 * ONE REQUEST for the batch (POST /leads/bulk). The server applies the
 * single-lead rules lead by lead and SKIPS what cannot take the action, with the
 * reason (a Booked / Invoiced / Closed job never changes stage in bulk); the dialog then reports "5 updated, 1 skipped" and each skip. Booked,
 * Invoiced and Closed are not offered: they have their own flows (the booking
 * dialog and its emails, invoices, an admin's close). Nothing here sends email.
 */

import React, { useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { useAction } from '../hooks/useAction';
import { formatJobNo } from '../lib/jobNumber';
import { stageColor, stageBg } from '../lib/stages';
import { repName } from './ClientCells';

/** The stages bulk may set (backend BULK_STAGES). Bad Lead and Lost have their own buttons. */
const CHANGE_STAGES = ['New Lead', 'In Progress', 'Opportunity', 'Bad Lead', 'Lost'];

type Pending =
  | { kind: 'grab' }
  | { kind: 'assign'; repId: string }
  | { kind: 'stage'; stage: string };

type Result = { updated: { id: string; name: string }[]; skipped: { id: string; name: string; reason: string }[] };

const userLabel = (u: any) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email;
const leadLabel = (l: any) => `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() || 'Unnamed';
const plural = (n: number, word = 'lead') => `${n} ${word}${n === 1 ? '' : 's'}`;

const barBtn: React.CSSProperties = {
  padding: '6px 11px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '7px',
  color: 'var(--crm-text)', cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
};
const barSelect: React.CSSProperties = { ...barBtn, fontWeight: 400, maxWidth: '160px' };

export function BulkActionBar({ selected, users, isAdmin, onClear, onDone }: {
  /** The selected leads (this page's rows). */
  selected: any[];
  users: any[];
  isAdmin: boolean;
  onClear: () => void;
  /** After a run: clear the selection and refetch. */
  onDone: () => void;
}) {
  const api = useApiClient();
  const run = useAction();
  const [pending, setPending] = useState<Pending | null>(null);
  // What the dialog is about — a snapshot, so the list stays put after the
  // selection clears on success.
  const [leads, setLeads] = useState<any[]>([]);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = (p: Pending) => { setPending(p); setLeads(selected); setReason(''); setResult(null); setError(null); };
  const close = () => { if (!run.pending) { setPending(null); setResult(null); } };

  const n = leads.length;
  const title = !pending ? '' : pending.kind === 'grab' ? `Grab ${plural(n)}?`
    : pending.kind === 'assign' ? `Assign ${plural(n)} to ${repName(users, pending.repId)}?`
    : pending.stage === 'Bad Lead' || pending.stage === 'Lost' ? `Mark ${plural(n)} as ${pending.stage}?`
    : `Move ${plural(n)} to ${pending.stage}?`;
  const askReason = pending?.kind === 'stage' && (pending.stage === 'Bad Lead' || pending.stage === 'Lost');

  // A failed request keeps its idempotency key (useAction), so Confirm again
  // is a retry of the same batch, not a second one.
  const confirm = async () => {
    if (!pending) return;
    setError(null);
    try {
      await run.run(async (key) => {
        const r = await api.withIdempotencyKey(key).bulkLeads({
          action: pending.kind,
          lead_ids: leads.map((l) => l.id),
          ...(pending.kind === 'assign' ? { assigned_rep_id: pending.repId } : {}),
          ...(pending.kind === 'stage' ? { stage: pending.stage, reason: askReason ? reason.trim() : undefined } : {}),
        });
        setResult(r.data ?? { updated: [], skipped: [] });
        onDone();
      });
    } catch (e: any) {
      setError(e?.message || 'Could not apply the action');
    }
  };

  return (
    <>
      {selected.length > 0 && (
        <div
          role="region" aria-label="Bulk actions"
          style={{
            position: 'sticky', bottom: 0, zIndex: 5, marginTop: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
            padding: '10px 12px', background: 'var(--crm-panel)', border: '1px solid var(--crm-accent-soft-line)', borderRadius: '10px',
            boxShadow: '0 -4px 16px var(--crm-shadow-color)',
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)', marginRight: '4px' }}>{selected.length} selected</span>
          <button style={barBtn} onClick={() => open({ kind: 'grab' })}>Grab</button>
          {isAdmin && (
            <select value="" aria-label="Assign to" style={barSelect} onChange={(e) => e.target.value && open({ kind: 'assign', repId: e.target.value })}>
              <option value="">Assign to…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
            </select>
          )}
          <button style={{ ...barBtn, color: 'var(--crm-red)' }} onClick={() => open({ kind: 'stage', stage: 'Bad Lead' })}>Mark Bad Lead</button>
          <button style={barBtn} onClick={() => open({ kind: 'stage', stage: 'Lost' })}>Mark Lost</button>
          <select value="" aria-label="Change stage" style={barSelect} onChange={(e) => e.target.value && open({ kind: 'stage', stage: e.target.value })}>
            <option value="">Change stage…</option>
            {CHANGE_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <button style={{ ...barBtn, background: 'transparent', border: 'none', color: 'var(--crm-muted)' }} onClick={onClear}>Clear</button>
        </div>
      )}

      {pending && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div
            role="dialog" aria-modal="true" aria-label={title}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '460px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', boxShadow: '0 12px 32px var(--crm-shadow-color)' }}
          >
            <div style={{ padding: '16px 18px 8px', fontSize: '16px', fontWeight: 700, color: 'var(--crm-text)' }}>
              {result ? `${result.updated.length} updated${result.skipped.length ? `, ${result.skipped.length} skipped` : ''}` : title}
            </div>

            <div style={{ padding: '0 18px', overflowY: 'auto', flex: 1 }}>
              {result ? (
                result.skipped.length > 0 ? (
                  <ul style={{ margin: '4px 0 0', padding: '0 0 0 18px', fontSize: '13px', color: 'var(--crm-text)' }}>
                    {result.skipped.map((s) => <li key={s.id} style={{ marginBottom: '4px' }}><b>{s.name}</b> — {s.reason}</li>)}
                  </ul>
                ) : (
                  <div style={{ fontSize: '13px', color: 'var(--crm-muted)' }}>Each lead's activity feed has the change. No emails were sent.</div>
                )
              ) : (
                <>
                  {pending.kind === 'grab' && (
                    <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: '8px' }}>Only unassigned leads are taken; any already assigned are skipped.</div>
                  )}
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {leads.map((l) => (
                      <li key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', borderBottom: '1px solid var(--crm-panel-2)', fontSize: '13px' }}>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', color: 'var(--crm-dim)', flexShrink: 0 }}>{formatJobNo(l)}</span>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--crm-text)' }}>{leadLabel(l)}</span>
                        {pending.kind === 'stage'
                          ? <span style={{ flexShrink: 0, fontSize: '10px', color: stageColor(l.stage), background: stageBg(l.stage), padding: '1px 7px', borderRadius: '4px' }}>{l.stage}</span>
                          : <span style={{ flexShrink: 0, fontSize: '11px', color: 'var(--crm-dim)' }}>{repName(users, l.assigned_rep_id) || 'Unassigned'}</span>}
                      </li>
                    ))}
                  </ul>
                  {askReason && (
                    <label style={{ display: 'block', marginTop: '10px', fontSize: '12px', color: 'var(--crm-muted)' }}>
                      Reason (optional, added to each lead's feed)
                      <textarea
                        value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000}
                        style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '4px', padding: '7px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '7px', color: 'var(--crm-text)', fontSize: '13px', resize: 'vertical' }}
                      />
                    </label>
                  )}
                  {error && <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--crm-red)' }}>{error}</div>}
                </>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '14px 18px' }}>
              {result ? (
                <button onClick={close} style={{ ...barBtn, background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none' }}>Done</button>
              ) : (
                <>
                  <button onClick={close} disabled={run.pending} style={barBtn}>Cancel</button>
                  <button
                    onClick={confirm} disabled={run.pending}
                    style={{ ...barBtn, border: 'none', color: 'var(--crm-on-fill)', background: pending.kind === 'stage' && pending.stage === 'Bad Lead' ? 'var(--crm-red-fill)' : 'var(--crm-accent-fill)', opacity: run.pending ? 0.6 : 1 }}
                  >
                    {run.pending ? 'Working…' : title.replace(/\?$/, '')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
